import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useCoreContext, useCoreStateContext } from "spark-core-dx/contexts";
import { useGenericManager } from "spark-core-dx/hooks";
import { ItemServiceFactory } from "../../../../services/ItemServiceFactory";
import { Operations } from "../../../../services/ItemServices";
import ConvokeEntityObjectManager from "../../../../managers/convoke/ConvokeEntityObjectManager";
import ConvokeSourceObjectManager from "../../../../managers/convoke/ConvokeSourceObjectManager";
import { deepCopy, normalizeDashboardQueryState } from "../../visualQueryStateUtils";
import { hydrateOption } from "../../eChartsHydrator";
import {
  buildVisualQueryOutputFields,
  hasExplicitVisualGrouping,
  parseVisualDesignSchema,
  sanitizeVisualConfiguration,
} from "../../visualDesignUtils";
import {
  VISUAL_EXECUTION_MODE,
  getVisualExecutionModeLabel,
  normalizeVisualExecutionMode,
  resolveVisualRuntimeExecutionMode,
} from "../../visualExecutionModes";
import { useSharedData } from "../../hooks/useSharedData";
import { useDashboardDomainContext } from "../../DashboardDomainProvider";

const TABLE_KIND = 1;
const VIEW_KIND = 2;
const MODEL_COLUMN_VALUE_KIND_FIELD = 1;
const LOAD_DATA_MODELS_AND_DATA_OPERATION = "LoadDataModelsAndData";
const EXECUTE_DATA_QUERY_OPERATION = "ExecuteDataQuery";
const EXECUTE_DATA_MODEL_WORKSPACE_ARTIFACT_QUERY_OPERATION = "ExecuteDataModelWorkspaceArtifactQuery";
const DATA_RUNTIME_NOT_LOADED_MESSAGE_CODE = "E3565";
const DATA_MODEL_WORKSPACE_ARTIFACT_NOT_PUBLISHED_MESSAGE_CODE = "E3566";
const DATA_MODEL_SERVER_EXECUTION_HOST_REMOTE_BATCH = 2;
const PROCESSOR_TYPE_REMOTE = 8;
const TABLE_VIEW_TYPE = "table";
const MATRIX_TYPE = "matrix";
const AI_RENDER_HTML_TYPE = "airenderhtml";
const HTML_CARD_TYPE = "htmlcard";

const createGuid = () => {
  if (typeof globalThis?.crypto?.randomUUID === "function") {
    return globalThis.crypto.randomUUID();
  }

  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
    const rand = Math.floor(Math.random() * 16);
    const value = char === "x" ? rand : ((rand & 0x3) | 0x8);
    return value.toString(16);
  });
};

const unwrapResult = (result) => (Array.isArray(result) ? result[0] ?? null : result ?? null);
const unwrapServiceResult = (result) => unwrapResult(result) ?? null;
const firstItem = (result) => {
  const normalized = unwrapResult(result);
  if (Array.isArray(normalized?.Items) && normalized.Items.length > 0) {
    return normalized.Items[0];
  }

  return normalized?.Item ?? null;
};

const getItemId = (item) => item?._id ?? item?.UniqueId ?? item?.Id ?? item?.id ?? null;
const getVisualTypeKey = (visualType) => String(visualType?.ShortName ?? visualType?.Type ?? "").trim().toLowerCase();
const getValidationDisplayMessages = (result) =>
  Object.values(unwrapServiceResult(result)?.ValidationErrors ?? {})
    .map((entry) => entry?.DisplayMessage ?? entry?.Content ?? "")
    .filter(Boolean);
const getOperationErrorMessage = (result, fallbackMessage) => {
  const response = unwrapServiceResult(result);
  const validationMessage = getValidationDisplayMessages(response)[0];
  return (
    validationMessage
    || response?.DisplayMessage
    || response?.Message?.DisplayMessage
    || response?.Message?.Content
    || fallbackMessage
  );
};

const PUBLISHED_ARTIFACT_UNAVAILABLE_MESSAGE =
  "No published model workspace artifact is available. Build and publish the linked workspace first.";

const getWarehouseDeployments = (warehouse) => {
  if (Array.isArray(warehouse?.WarehouseDeployments)) {
    return warehouse.WarehouseDeployments;
  }

  if (Array.isArray(warehouse?.Deployments)) {
    return warehouse.Deployments;
  }

  return [];
};

const getTableDefinition = (model) => model?.TableDefinition ?? null;
const getSemanticDefinition = (model) => model?.SemanticDefinition ?? {};
const getModelSourceType = (model) => Number(getTableDefinition(model)?.SourceType ?? 0);
const getModelReferenceId = (model) => String(getTableDefinition(model)?.ReferenceId ?? "").trim();
const getModelWarehouseId = (model) => String(getTableDefinition(model)?.WarehouseId ?? "").trim();
const getModelWarehouseDeploymentId = (model) =>
  String(getTableDefinition(model)?.WarehouseDeploymentId ?? "").trim();
const getModelFilterDefinition = (model) => getTableDefinition(model)?.ConvokeQueryFilterDefinition ?? null;
const getModelSemanticColumns = (model) =>
  Array.isArray(getSemanticDefinition(model)?.Columns) ? getSemanticDefinition(model).Columns : [];
const getModelSemanticMeasures = (model) =>
  Array.isArray(getSemanticDefinition(model)?.Measures) ? getSemanticDefinition(model).Measures : [];
const getDataModelWorkspaceId = (model) => String(model?.DataModelWorkspaceId ?? "").trim();

const inferFieldType = (dataTypeName) => {
  const normalized = String(dataTypeName ?? "").toLowerCase();

  if (normalized.includes("bool")) {
    return "CheckBox";
  }

  if (normalized.includes("date") || normalized.includes("time")) {
    return "Date";
  }

  return "TextBox";
};

const mapFilterableColumn = (column, fieldName) => ({
  FieldName: fieldName,
  ColumnName: fieldName,
  SourceFieldName: column?.SourceFieldName || fieldName,
  BindingKind: column?.BindingKind || "column",
  DisplayName: column?.DisplayName || fieldName,
  DataTypeName: column?.DataTypeName || "",
  FieldType: inferFieldType(column?.DataTypeName),
  DataJson: column?.DataJson || "",
  Format: column?.Format,
  CanFilter: column?.CanFilter !== false,
});

const mapModelSemanticColumn = (column) => {
  const fieldName = String(column?.ValueDefinition?.Value ?? "").trim();
  const bindingName = String(column?.ColumnName ?? "").trim() || fieldName;

  if (
    !bindingName ||
    !fieldName ||
    Number(column?.ValueDefinition?.ValueKind ?? 0) !== MODEL_COLUMN_VALUE_KIND_FIELD ||
    column?.IsDeleted === true ||
    column?.IsActive === false
  ) {
    return null;
  }

  return mapFilterableColumn(
    {
      ...column,
      SourceFieldName: fieldName,
    },
    bindingName
  );
};

const mapModelSemanticPublishedColumn = (column) => {
  const bindingName = String(column?.ColumnName ?? "").trim();

  if (!bindingName || column?.IsDeleted === true || column?.IsActive === false) {
    return null;
  }

  const sourceFieldName = String(column?.ValueDefinition?.Value ?? "").trim();

  return {
    FieldName: bindingName,
    ColumnName: bindingName,
    SourceFieldName: sourceFieldName || bindingName,
    BindingKind: "column",
    DisplayName: column?.DisplayName || bindingName,
    DataTypeName: column?.DataTypeName || "",
    Format: column?.Format,
    SemanticType: column?.SemanticType || "",
    CanFilter: column?.CanFilter !== false,
    CanSort: column?.CanSort !== false,
    CanGroup: column?.CanGroup !== false,
    IsVisible: column?.IsVisible !== false,
    Aggregation: "none",
  };
};

const mapModelSemanticMeasure = (measure) => {
  const fieldName = String(measure?.MeasureName ?? "").trim();

  if (!fieldName || measure?.IsDeleted === true || measure?.IsActive === false) {
    return null;
  }

  return {
    FieldName: fieldName,
    ColumnName: fieldName,
    SourceFieldName: String(measure?.MeasureDefinition?.TargetName ?? "").trim() || fieldName,
    BindingKind: "measure",
    DisplayName: measure?.DisplayName || fieldName,
    DataTypeName: measure?.DataTypeName || "",
    Format: measure?.Format,
    SemanticType: "measure",
    CanFilter: false,
    CanSort: true,
    CanGroup: false,
    IsVisible: measure?.IsVisible !== false,
    Aggregation: String(measure?.AggregationKind ?? "").trim(),
  };
};

const buildVisualFieldCatalog = (model, queryState) => {
  const selectedModelColumns = getModelSemanticColumns(model)
    .map((column) => mapModelSemanticColumn(column))
    .filter((column) => !!column?.FieldName);
  const selectedModelSemanticColumns = getModelSemanticColumns(model)
    .map((column) => mapModelSemanticPublishedColumn(column))
    .filter((column) => !!column?.FieldName);
  const selectedModelMeasures = getModelSemanticMeasures(model)
    .map((measure) => mapModelSemanticMeasure(measure))
    .filter((measure) => !!measure?.FieldName);
  const selectedQueryOutputFields = buildVisualQueryOutputFields(
    queryState,
    selectedModelSemanticColumns,
    selectedModelMeasures
  );

  const seen = new Set();
  const selectedFieldCatalog = [];
  [...selectedModelSemanticColumns, ...selectedModelMeasures, ...selectedQueryOutputFields].forEach((field) => {
    const fieldName = String(field?.FieldName ?? "").trim();
    if (!fieldName) {
      return;
    }

    const dedupeKey = fieldName.toLowerCase();
    if (seen.has(dedupeKey)) {
      return;
    }

    seen.add(dedupeKey);
    selectedFieldCatalog.push(field);
  });

  return {
    selectedFieldCatalog,
    selectedBindingFields: hasExplicitVisualGrouping(queryState) ? selectedQueryOutputFields : selectedModelColumns,
  };
};

const combineFilterNodes = (left, right, method = "&&") => {
  if (!left && !right) {
    return null;
  }

  if (!left) {
    return deepCopy(right);
  }

  if (!right) {
    return deepCopy(left);
  }

  return {
    Name: null,
    Method: method,
    Value: null,
    CheckCase: false,
    Left: deepCopy(left),
    Right: deepCopy(right),
  };
};

const normalizeSemanticType = (column) => {
  const semanticType = String(column?.SemanticType ?? "").trim().toLowerCase();
  if (semanticType) {
    return semanticType;
  }

  const dataTypeName = String(column?.DataTypeName ?? "").trim().toLowerCase();
  if (
    dataTypeName.includes("int") ||
    dataTypeName.includes("decimal") ||
    dataTypeName.includes("double") ||
    dataTypeName.includes("float") ||
    dataTypeName.includes("number")
  ) {
    return "measure";
  }

  if (dataTypeName.includes("date") || dataTypeName.includes("time")) {
    return "time";
  }

  return "dimension";
};

const buildModelColumnFromSemanticColumn = (column, index = 0) => {
  const sourceFieldName = String(column?.ValueDefinition?.Value ?? "").trim();
  if (
    !sourceFieldName ||
    Number(column?.ValueDefinition?.ValueKind ?? 0) !== MODEL_COLUMN_VALUE_KIND_FIELD ||
    column?.IsDeleted === true ||
    column?.IsActive === false
  ) {
    return null;
  }

  const sortOrder = Number.isFinite(Number(column?.SortOrder)) ? Number(column.SortOrder) : index;
  const columnName = String(column?.ColumnName ?? "").trim() || sourceFieldName;

  return {
    FieldName: columnName,
    ColumnName: columnName,
    SourceFieldName: sourceFieldName,
    DisplayName: column?.DisplayName || columnName,
    Description: column?.Description || "",
    DataTypeName: column?.DataTypeName || "",
    Format: column?.Format || "",
    SemanticType: column?.SemanticType || "none",
    GridSequence: sortOrder,
    SortOrder: sortOrder,
    DisplaySequence: sortOrder + 1,
    CanSort: column?.CanSort ?? true,
    CanFilter: column?.CanFilter ?? true,
    CanGroup: column?.CanGroup ?? true,
    ShowOnGrid: column?.IsVisible ?? true,
    IsActive: true,
    IsDeleted: false,
  };
};

const getRealtimeModelColumns = (model) =>
  getModelSemanticColumns(model)
    .map((column, index) => buildModelColumnFromSemanticColumn(column, index))
    .filter(Boolean)
    .sort((a, b) => Number(a?.SortOrder ?? 0) - Number(b?.SortOrder ?? 0));

const buildRealtimeFieldMap = (model) =>
  getRealtimeModelColumns(model).reduce((lookup, column) => {
    const bindingName = String(column?.ColumnName ?? column?.FieldName ?? "").trim();
    const sourceFieldName = String(column?.SourceFieldName ?? "").trim();
    if (!bindingName || !sourceFieldName) {
      return lookup;
    }

    lookup.set(bindingName.toLowerCase(), sourceFieldName);
    return lookup;
  }, new Map());

const resolveRealtimeSourceFieldName = (fieldName, fieldLookup) => {
  const normalized = String(fieldName ?? "").trim();
  if (!normalized) {
    return "";
  }

  return fieldLookup.get(normalized.toLowerCase()) ?? "";
};

const translateRealtimeFieldList = (fieldNames = [], fieldLookup) =>
  Array.from(
    new Set(
      (Array.isArray(fieldNames) ? fieldNames : [])
        .map((fieldName) => resolveRealtimeSourceFieldName(fieldName, fieldLookup))
        .filter(Boolean)
    )
  );

const isConjunctionMethod = (method) => method === "&&" || method === "||";

const translateRealtimeFilterNode = (node, fieldLookup) => {
  if (!node || typeof node !== "object") {
    return node ?? null;
  }

  const nextNode = deepCopy(node);
  const name = String(nextNode?.Name ?? "").trim();
  if (name) {
    const translated = resolveRealtimeSourceFieldName(name, fieldLookup);
    if (!translated) {
      return null;
    }

    nextNode.Name = translated;
  }

  if (nextNode?.Left) {
    nextNode.Left = translateRealtimeFilterNode(nextNode.Left, fieldLookup);
  }

  if (nextNode?.Right) {
    nextNode.Right = translateRealtimeFilterNode(nextNode.Right, fieldLookup);
  }

  const method = String(nextNode?.Method ?? "");
  if (isConjunctionMethod(method)) {
    if (!nextNode?.Left && !nextNode?.Right) {
      return null;
    }

    if (!nextNode?.Left) {
      return nextNode.Right ?? null;
    }

    if (!nextNode?.Right) {
      return nextNode.Left ?? null;
    }

    return nextNode;
  }

  if (!String(nextNode?.Name ?? "").trim()) {
    return null;
  }

  return nextNode;
};

const translateCanonicalToSource = (canonicalMap = {}, fieldLookup) =>
  Object.entries(canonicalMap).reduce((next, [canonical, fieldName]) => {
    const translatedFieldName = resolveRealtimeSourceFieldName(fieldName, fieldLookup);
    if (!translatedFieldName) {
      return next;
    }

    next[canonical] = translatedFieldName;
    return next;
  }, {});

const buildCanonicalToSource = (visual, modelColumns = []) => {
  const next = {};
  const byCanonical = new Map();
  const visualColumns = Array.isArray(visual?.VisualColumns) ? visual.VisualColumns : [];

  visualColumns.forEach((column) => {
    const canonical = String(column?.Canonical ?? "").trim();
    const fieldName = String(column?.FieldName ?? "").trim();
    if (!canonical || canonical.toLowerCase() === "none" || !fieldName) {
      return;
    }

    const current = byCanonical.get(canonical);
    if (!current || column?.IsDefaultForCanonical) {
      byCanonical.set(canonical, {
        fieldName,
        isDefault: !!column?.IsDefaultForCanonical,
      });
    }
  });

  byCanonical.forEach((value, canonical) => {
    next[canonical] = value.fieldName;
  });

  const normalizedColumns = (Array.isArray(modelColumns) ? modelColumns : []).filter((column) => column?.FieldName);
  const measures = normalizedColumns.filter((column) => normalizeSemanticType(column) === "measure");
  const dimensions = normalizedColumns.filter((column) => normalizeSemanticType(column) !== "measure");

  if (!next.x && dimensions[0]?.FieldName) {
    next.x = dimensions[0].FieldName;
  }
  if (!next.y && measures[0]?.FieldName) {
    next.y = measures[0].FieldName;
  }
  if (!next.name && dimensions[0]?.FieldName) {
    next.name = dimensions[0].FieldName;
  }
  if (!next.value && measures[0]?.FieldName) {
    next.value = measures[0].FieldName;
  }
  if (!next.SeriesName && dimensions[1]?.FieldName) {
    next.SeriesName = dimensions[1].FieldName;
  }

  return next;
};

const buildPreviewSearchModel = (model, visual) => {
  const referenceId = getModelReferenceId(model);
  if (!referenceId) {
    return null;
  }

  const fieldLookup = buildRealtimeFieldMap(model);
  const nextQuery = deepCopy(getModelFilterDefinition(model) ?? {});
  const modelColumns = getRealtimeModelColumns(model)
    .filter((column) => column?.FieldName)
    .map((column) => column.FieldName);
  const visualColumns = (Array.isArray(visual?.VisualColumns) ? visual.VisualColumns : [])
    .map((column) => column?.FieldName)
    .filter(Boolean);
  const visualQueryState = normalizeDashboardQueryState(visual?.DefaultQueryState, {
    ModelKind: Number(visual?.ModelKind ?? 0),
    ModelId: visual?.ModelId ?? "",
  });
  const explicitColumns = Array.isArray(visualQueryState?.SelectColumns) ? visualQueryState.SelectColumns : [];
  const selectColumns = translateRealtimeFieldList([...modelColumns, ...visualColumns, ...explicitColumns], fieldLookup);

  if (selectColumns.length > 0) {
    nextQuery.SelectColumns = selectColumns;
  }

  nextQuery.RootFilterNode = combineFilterNodes(
    translateRealtimeFilterNode(nextQuery?.RootFilterNode, fieldLookup),
    translateRealtimeFilterNode(visualQueryState?.RootFilterNode, fieldLookup)
  );

  if (getModelSourceType(model) === 1) {
    nextQuery.SourceObjectId = nextQuery.SourceObjectId ?? referenceId;
  }

  if (getModelSourceType(model) === 2) {
    nextQuery.EntityObjectId = nextQuery.EntityObjectId ?? referenceId;
    nextQuery.WarehouseId = nextQuery.WarehouseId ?? getModelWarehouseId(model) ?? "";
    nextQuery.WarehouseDeploymentId =
      nextQuery.WarehouseDeploymentId ??
      getModelWarehouseDeploymentId(model) ??
      "";
  }

  return Object.keys(nextQuery).length > 0 ? nextQuery : null;
};

const buildPreviewDashboard = (visual, pageKey) => ({
  DraftDetails: {
    Pages: [
      {
        Key: pageKey || "preview-page",
        Name: "Preview",
        ColumnCount: 10,
        Visuals: [visual],
      },
    ],
  },
});

export const useVisualPreviewData = ({
  visual,
  pageKey = null,
  refreshCycleId = 0,
  onRefreshSettled = null,
}) => {
  const { visualTypes } = useSharedData();
  const {
    activeDashboard,
    activeDraftWorkspaceSummary,
    activePublishedWorkspaceSummary,
    isViewMode,
  } = useDashboardDomainContext();
  const coreContext = useCoreContext();
  const coreState = useCoreStateContext();
  const itemServiceFactory = useMemo(() => new ItemServiceFactory(), []);
  const dashboardMgr = useGenericManager(itemServiceFactory.ItemServiceEnum.Dashboard, false);
  const modelMgr = useGenericManager(itemServiceFactory.ItemServiceEnum.DataModel, false);
  const workspaceMgr = useGenericManager(itemServiceFactory.ItemServiceEnum.DataModelWorkspace, false);
  const dataQueryMgr = useGenericManager(itemServiceFactory.ItemServiceEnum.DataQuery, false);
  const warehouseMgr = useGenericManager(itemServiceFactory.ItemServiceEnum.Warehouse, false);
  const entityObjectMgr = useGenericManager(itemServiceFactory.ItemServiceEnum.EntityObject, false);

  const fallbackVisualIdRef = useRef(createGuid());
  const requestIdRef = useRef(0);
  const coreContextRef = useRef(coreContext);
  const coreStateRef = useRef(coreState);
  const dashboardMgrRef = useRef(dashboardMgr);
  const modelMgrRef = useRef(modelMgr);
  const workspaceMgrRef = useRef(workspaceMgr);
  const dataQueryMgrRef = useRef(dataQueryMgr);
  const warehouseMgrRef = useRef(warehouseMgr);
  const entityObjectMgrRef = useRef(entityObjectMgr);
  const lastSettledRefreshCycleRef = useRef(0);
  const [state, setState] = useState({
    renderModel: null,
    isLoading: false,
    status: "Select a model and visual type to preview this visual.",
    error: "",
  });

  useEffect(() => {
    coreContextRef.current = coreContext;
  }, [coreContext]);

  useEffect(() => {
    coreStateRef.current = coreState;
  }, [coreState]);

  useEffect(() => {
    dashboardMgrRef.current = dashboardMgr;
  }, [dashboardMgr]);

  useEffect(() => {
    modelMgrRef.current = modelMgr;
  }, [modelMgr]);

  useEffect(() => {
    workspaceMgrRef.current = workspaceMgr;
  }, [workspaceMgr]);

  useEffect(() => {
    dataQueryMgrRef.current = dataQueryMgr;
  }, [dataQueryMgr]);

  useEffect(() => {
    warehouseMgrRef.current = warehouseMgr;
  }, [warehouseMgr]);

  useEffect(() => {
    entityObjectMgrRef.current = entityObjectMgr;
  }, [entityObjectMgr]);

  const getWorkspaceProcessorTypeOverride = useCallback((workspaceSummary) => (
    Number(workspaceSummary?.workspace?.ServerExecutionHost ?? 0) === DATA_MODEL_SERVER_EXECUTION_HOST_REMOTE_BATCH
      ? PROCESSOR_TYPE_REMOTE
      : undefined
  ), []);

  const previewVisualId = useMemo(
    () => String(visual?.VisualId ?? visual?._id ?? fallbackVisualIdRef.current),
    [visual?._id, visual?.VisualId]
  );

  const previewSignature = useMemo(() => {
    if (!visual) {
      return "";
    }

    try {
        return JSON.stringify({
          VisualId: previewVisualId,
          UniqueId: visual?.UniqueId ?? previewVisualId,
          ModelKind: visual?.ModelKind,
          ModelId: visual?.ModelId,
          VisualTypeId: visual?.VisualTypeId,
          ExecutionMode: visual?.ExecutionMode,
          VisualColumns: visual?.VisualColumns ?? [],
          RenderSettings: visual?.RenderSettings ?? {},
          DefaultQueryState: visual?.DefaultQueryState ?? {},
      });
    } catch {
      return `${previewVisualId}:${visual?.ModelId ?? ""}:${visual?.VisualTypeId ?? ""}`;
    }
  }, [
    previewVisualId,
    visual?.DefaultQueryState,
    visual?.ModelId,
    visual?.ModelKind,
    visual?.RenderSettings,
    visual?.UniqueId,
    visual?.VisualColumns,
    visual?.VisualTypeId,
  ]);

  const previewVisual = useMemo(() => {
    if (!visual) {
      return null;
    }

    const nextVisual = deepCopy(visual);
    nextVisual.VisualId = previewVisualId;
    nextVisual.UniqueId = visual?.UniqueId ?? previewVisualId;
    return nextVisual;
  }, [previewSignature, previewVisualId, visual]);

  const settleRefreshCycle = useCallback((cycleId) => {
    const normalizedCycleId = Number(cycleId ?? 0);
    if (!onRefreshSettled || !previewVisualId || !Number.isFinite(normalizedCycleId) || normalizedCycleId <= 0) {
      return;
    }

    if (lastSettledRefreshCycleRef.current === normalizedCycleId) {
      return;
    }

    lastSettledRefreshCycleRef.current = normalizedCycleId;
    onRefreshSettled(previewVisualId, normalizedCycleId);
  }, [onRefreshSettled, previewVisualId]);

  const selectedVisualType = useMemo(
    () => (visualTypes ?? []).find((item) => String(item?._id ?? item?.Value) === String(previewVisual?.VisualTypeId ?? "")) ?? null,
    [previewVisual?.VisualTypeId, visualTypes]
  );
  const selectedVisualSchema = useMemo(
    () => parseVisualDesignSchema(selectedVisualType, previewVisual),
    [previewVisual, selectedVisualType]
  );

  const loadShell = useCallback(async (nextVisual) => {
    const result = await dashboardMgrRef.current?.RunOperation?.({
      operationName: "LoadVisualOptions",
      item: buildPreviewDashboard(nextVisual, pageKey),
      queryParams: { draft: true },
      isItem: true,
    });

    const items = Array.isArray(result?.Items) ? result.Items : [];
    return items.find((item) => String(item?._id) === String(nextVisual?.VisualId)) ?? items[0] ?? null;
  }, [pageKey]);

  const loadRows = useCallback(async (model, nextVisual) => {
    const sourceType = getModelSourceType(model);
    const searchModel = buildPreviewSearchModel(model, nextVisual);
    const referenceId = getModelReferenceId(model);

    if (!referenceId) {
      return [];
    }

    if (sourceType === 1) {
      const mgr = new ConvokeSourceObjectManager(
        referenceId,
        coreContextRef.current,
        coreStateRef.current,
        Operations.SourceObjectItem.SearchSourceObject,
        true
      );

      const result = await mgr.search({
        id: referenceId,
        queryParams: { search: true },
        take: 50000,
        forceUpdate: true,
        ...(searchModel ? { searchModel } : {}),
      });

      return Array.isArray(result?.Items) ? result.Items : [];
    }

    if (sourceType === 2) {
      const mgr = new ConvokeEntityObjectManager(
        referenceId,
        coreContextRef.current,
        coreStateRef.current,
        Operations.EntityObjectItem.SearchEntityObject,
        true
      );

      let warehouseId = searchModel?.WarehouseId ?? getModelWarehouseId(model) ?? "";
      const warehouseDeploymentId = searchModel?.WarehouseDeploymentId ?? getModelWarehouseDeploymentId(model) ?? "";

      if (!warehouseId && warehouseDeploymentId) {
        const entityResult = await entityObjectMgrRef.current?.getItem?.(referenceId);
        const schemaId = firstItem(entityResult)?.SchemaId ?? "";

        if (schemaId) {
          const warehouseResult = await warehouseMgrRef.current?.Query?.({
            model: "x => x.IsActive == true && x.IsDeleted == false && x.SchemaId == schemaId",
            params: { schemaId },
          });

          const warehouseItems = Array.isArray(warehouseResult?.Items) ? warehouseResult.Items : [];
          const owningWarehouse = warehouseItems.find((warehouse) =>
            getWarehouseDeployments(warehouse).some(
              (deployment) => String(getItemId(deployment)) === String(warehouseDeploymentId)
            )
          );

          warehouseId = String(getItemId(owningWarehouse) ?? "");
        }
      }

      if (searchModel && warehouseId && !searchModel.WarehouseId) {
        searchModel.WarehouseId = warehouseId;
      }

      const result = await mgr.search({
        id: referenceId,
        queryParams: {
          search: true,
          ...(warehouseId ? { wId: warehouseId } : {}),
          ...(warehouseDeploymentId ? { wdId: warehouseDeploymentId } : {}),
        },
        take: 50000,
        forceUpdate: true,
        ...(searchModel ? { searchModel } : {}),
      });

      return Array.isArray(result?.Items) ? result.Items : [];
    }

    return [];
  }, []);

  const loadServerRows = useCallback(async (model, nextVisual, queryState, executionMode) => {
    const workspaceId = getDataModelWorkspaceId(model);
    const dashboardWorkspaceId = String(
      (
        isViewMode
          ? (activeDashboard?.PublishedDataModelWorkspaceId ?? activeDashboard?.DataModelWorkspaceId)
          : activeDashboard?.DataModelWorkspaceId
      ) ?? ""
    ).trim();
    if (dashboardWorkspaceId && workspaceId && dashboardWorkspaceId !== workspaceId) {
      throw new Error("The selected data model belongs to a different model workspace than this dashboard.");
    }

    if (!workspaceId) {
      throw new Error("The selected data model is not assigned to a model workspace.");
    }

    if (executionMode === VISUAL_EXECUTION_MODE.SERVER && !dashboardWorkspaceId) {
      throw new Error("This dashboard is not linked to a model workspace.");
    }

    const queryPayload = {
      ...deepCopy(queryState ?? {}),
      ModelId: nextVisual?.ModelId ?? getItemId(model),
      ModelKind: Number(nextVisual?.ModelKind ?? model?.ModelKind ?? 0),
    };
    const activeWorkspaceSummary = executionMode === VISUAL_EXECUTION_MODE.SERVER
      ? (activePublishedWorkspaceSummary ?? activeDraftWorkspaceSummary)
      : (activeDraftWorkspaceSummary ?? activePublishedWorkspaceSummary);
    const processorType = getWorkspaceProcessorTypeOverride(activeWorkspaceSummary);

    const executeWorkspaceQuery = async () => {
      const rawResponse = await dataQueryMgrRef.current?.RunOperation?.({
        operationName: EXECUTE_DATA_QUERY_OPERATION,
        item: queryPayload,
        queryParams: { workspaceId },
        isItem: true,
        processorType,
      });
      const response = unwrapServiceResult(rawResponse);
      if (!response?.Success) {
        const error = new Error(getOperationErrorMessage(response, "Unable to execute the visual data query."));
        error.code = String(response?.Message?.Code ?? "").trim().toUpperCase();
        throw error;
      }

      return response?.Item ?? response?.Items?.[0] ?? null;
    };

    const executePublishedQuery = async () => {
      const rawResponse = await dataQueryMgrRef.current?.RunOperation?.({
        operationName: EXECUTE_DATA_MODEL_WORKSPACE_ARTIFACT_QUERY_OPERATION,
        item: queryPayload,
        queryParams: { workspaceId },
        isItem: true,
        processorType,
      });
      const response = unwrapServiceResult(rawResponse);
      if (!response?.Success) {
        const errorCode = String(response?.Message?.Code ?? "").trim().toUpperCase();
        const error = new Error(
          errorCode === DATA_MODEL_WORKSPACE_ARTIFACT_NOT_PUBLISHED_MESSAGE_CODE
            ? PUBLISHED_ARTIFACT_UNAVAILABLE_MESSAGE
            : getOperationErrorMessage(response, "Unable to execute the published visual data query.")
        );
        error.code = String(response?.Message?.Code ?? "").trim().toUpperCase();
        throw error;
      }

      return response?.Item ?? response?.Items?.[0] ?? null;
    };

    if (executionMode === VISUAL_EXECUTION_MODE.SERVER) {
      return await executePublishedQuery();
    }

    try {
      return await executeWorkspaceQuery();
    } catch (error) {
      const errorCode = String(error?.code ?? "").trim().toUpperCase();
      if (errorCode !== DATA_RUNTIME_NOT_LOADED_MESSAGE_CODE) {
        throw error;
      }

      const rawLoadResponse = await workspaceMgrRef.current?.RunOperation?.({
        operationName: LOAD_DATA_MODELS_AND_DATA_OPERATION,
        id: workspaceId,
        isItem: true,
        processorType,
      });
      const loadResponse = unwrapServiceResult(rawLoadResponse);
      if (!loadResponse?.Success) {
        throw new Error(getOperationErrorMessage(loadResponse, "Unable to load the model workspace runtime."));
      }

      return await executeWorkspaceQuery();
    }
  }, [
    activeDashboard?.DataModelWorkspaceId,
    activeDashboard?.PublishedDataModelWorkspaceId,
    activeDraftWorkspaceSummary,
    activePublishedWorkspaceSummary,
    getWorkspaceProcessorTypeOverride,
    isViewMode,
  ]);

  useEffect(() => {
    let alive = true;
    const settleCurrentRefresh = () => settleRefreshCycle(refreshCycleId);

    const run = async () => {
      if (!previewVisual) {
        setState({
          renderModel: null,
          isLoading: false,
          status: "Select a model and visual type to preview this visual.",
          error: "",
        });
        settleCurrentRefresh();
        return;
      }

      if (!previewVisual?.VisualTypeId) {
        setState({
          renderModel: null,
          isLoading: false,
          status: "Select a visual type to preview this visual.",
          error: "",
        });
        settleCurrentRefresh();
        return;
      }

      const visualTypeKey = getVisualTypeKey(selectedVisualType);
      if (visualTypeKey === TABLE_VIEW_TYPE) {
        setState({
          renderModel: null,
          isLoading: false,
          status: "Table Visual Coming soon.",
          error: "",
        });
        settleCurrentRefresh();
        return;
      }

      if (visualTypeKey === MATRIX_TYPE) {
        setState({
          renderModel: null,
          isLoading: false,
          status: "Matrix Visual Coming soon.",
          error: "",
        });
        settleCurrentRefresh();
        return;
      }

      if (visualTypeKey === AI_RENDER_HTML_TYPE) {
        setState({
          renderModel: null,
          isLoading: false,
          status: "AI Rendering Visual Coming soon.",
          error: "",
        });
        settleCurrentRefresh();
        return;
      }

      if (visualTypeKey === HTML_CARD_TYPE) {
        setState({
          renderModel: null,
          isLoading: false,
          status: "HTML Card Visual Coming soon.",
          error: "",
        });
        settleCurrentRefresh();
        return;
      }

      if (!previewVisual?.ModelId) {
        setState({
          renderModel: null,
          isLoading: false,
          status: "Select a model to preview this visual.",
          error: "",
        });
        settleCurrentRefresh();
        return;
      }

      const modelKind = Number(previewVisual?.ModelKind ?? 0);
      const executionMode = normalizeVisualExecutionMode(
        modelKind,
        previewVisual?.ExecutionMode,
        previewVisual?.DefaultQueryState?.SupportsRealTime ?? true
      );
      const runtimeExecutionMode = resolveVisualRuntimeExecutionMode(executionMode, isViewMode);

      if (executionMode === VISUAL_EXECUTION_MODE.REALTIME && modelKind === VIEW_KIND) {
        setState({
          renderModel: null,
          isLoading: false,
          status: "RealTime preview is only available for table models.",
          error: "",
        });
        settleCurrentRefresh();
        return;
      }

      if (
        runtimeExecutionMode !== VISUAL_EXECUTION_MODE.REALTIME
        && runtimeExecutionMode !== VISUAL_EXECUTION_MODE.SERVER
        && runtimeExecutionMode !== VISUAL_EXECUTION_MODE.SERVER_WORKSPACE
      ) {
        setState({
          renderModel: null,
          isLoading: false,
          status: `${getVisualExecutionModeLabel(executionMode)} preview is not wired yet.`,
          error: "",
        });
        settleCurrentRefresh();
        return;
      }

      if (executionMode === VISUAL_EXECUTION_MODE.REALTIME && modelKind !== TABLE_KIND) {
        setState({
          renderModel: null,
          isLoading: false,
          status: "This visual is not bound to a supported model yet.",
          error: "",
        });
        settleCurrentRefresh();
        return;
      }

      if (
        (runtimeExecutionMode === VISUAL_EXECUTION_MODE.SERVER || runtimeExecutionMode === VISUAL_EXECUTION_MODE.SERVER_WORKSPACE)
        && modelKind !== TABLE_KIND
        && modelKind !== VIEW_KIND
      ) {
        setState({
          renderModel: null,
          isLoading: false,
          status: "This visual is not bound to a supported model yet.",
          error: "",
        });
        settleCurrentRefresh();
        return;
      }

      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;

      setState((prev) => {
        const preserveExistingRender = Number(refreshCycleId ?? 0) > 0 && !!prev?.renderModel;
        return {
          renderModel: preserveExistingRender ? prev.renderModel : null,
          isLoading: preserveExistingRender ? false : true,
          status: preserveExistingRender ? prev?.status ?? "" : "Preparing your preview...",
          error: "",
        };
      });

      try {
        const modelResult = await modelMgrRef.current?.getItem?.(previewVisual.ModelId);
        const model = firstItem(modelResult);
        if (!model) {
          throw new Error("Unable to load the selected data model.");
        }

        let sanitizedVisual = previewVisual;
        let rows = [];
        let shell = null;

        if (executionMode === VISUAL_EXECUTION_MODE.REALTIME) {
          const modelColumns = getRealtimeModelColumns(model);
          const fieldLookup = buildRealtimeFieldMap(model);
          sanitizedVisual = sanitizeVisualConfiguration(previewVisual, selectedVisualSchema, modelColumns);

          [shell, rows] = await Promise.all([
            loadShell(sanitizedVisual),
            loadRows(model, sanitizedVisual),
          ]);

          if (!alive || requestIdRef.current !== requestId) {
            return;
          }

          if (!shell) {
            throw new Error("Unable to build the chart preview.");
          }

          const canonicalToSource = translateCanonicalToSource(
            buildCanonicalToSource(sanitizedVisual, modelColumns),
            fieldLookup
          );
          const hydratedOptionJson = hydrateOption(shell, rows, canonicalToSource);
          const renderModel = {
            ...shell,
            OptionJson: hydratedOptionJson,
          };

          setState({
            renderModel,
            isLoading: false,
            status: rows.length === 0 ? "No data matched this visual." : "",
            error: "",
          });
          settleCurrentRefresh();
          return;
        }

        const normalizedQueryState = normalizeDashboardQueryState(previewVisual?.DefaultQueryState, {
          ModelKind: modelKind,
          ModelId: previewVisual?.ModelId ?? getItemId(model) ?? "",
        });
        const { selectedFieldCatalog, selectedBindingFields } = buildVisualFieldCatalog(model, normalizedQueryState);
        sanitizedVisual = sanitizeVisualConfiguration(
          previewVisual,
          selectedVisualSchema,
          selectedFieldCatalog,
          selectedBindingFields
        );
        const serverQueryState = normalizeDashboardQueryState(sanitizedVisual?.DefaultQueryState, {
          ModelKind: modelKind,
          ModelId: sanitizedVisual?.ModelId ?? getItemId(model) ?? "",
        });

        const [nextShell, queryResult] = await Promise.all([
          loadShell(sanitizedVisual),
          loadServerRows(model, sanitizedVisual, serverQueryState, runtimeExecutionMode),
        ]);
        shell = nextShell;
        rows = Array.isArray(queryResult?.Data) ? queryResult.Data : [];

        if (!alive || requestIdRef.current !== requestId) {
          return;
        }

        if (!shell) {
          throw new Error("Unable to build the chart preview.");
        }

        const canonicalToSource = buildCanonicalToSource(sanitizedVisual, selectedBindingFields);
        const hydratedOptionJson = hydrateOption(shell, rows, canonicalToSource);
        const renderModel = {
          ...shell,
          OptionJson: hydratedOptionJson,
        };

        setState({
          renderModel,
          isLoading: false,
          status: rows.length === 0 ? "No data matched this visual." : "",
          error: "",
        });
        settleCurrentRefresh();
      } catch (error) {
        if (!alive || requestIdRef.current !== requestId) {
          return;
        }

        setState((prev) => ({
          renderModel: prev?.renderModel ?? null,
          isLoading: false,
          status: "",
          error: prev?.renderModel ? "" : String(error?.message || error || "Unable to render this visual."),
        }));
        settleCurrentRefresh();
      }
    };

    void run();

    return () => {
      alive = false;
    };
  }, [isViewMode, loadRows, loadServerRows, loadShell, previewSignature, refreshCycleId, selectedVisualSchema, selectedVisualType, settleRefreshCycle]);

  return state;
};
